import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { menuCategories } from '../data/mock';

const MenuSection = () => {
  const [selectedCategory, setSelectedCategory] = useState(menuCategories[0]);

  // Support direct URL hash navigation (#menu or #explorar-menu)
  useEffect(() => {
    const handleHashScroll = () => {
      const hash = window.location.hash;
      if (hash === '#menu' || hash === '#explorar-menu') {
        const el = document.getElementById('menu') || document.getElementById('explorar-menu');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
        }
      }
    };

    if (window.location.hash === '#menu' || window.location.hash === '#explorar-menu') {
      // Small timeout to allow DOM & animations to settle
      const timer = setTimeout(handleHashScroll, 200);
      return () => clearTimeout(timer);
    }

    window.addEventListener('hashchange', handleHashScroll);
    return () => window.removeEventListener('hashchange', handleHashScroll);
  }, []);

  return (
    <section 
      id="menu" 
      className="py-12 sm:py-20 px-4 sm:px-6 bg-zinc-950 relative overflow-hidden scroll-mt-0"
    >
      {/* Secondary anchor alias for #explorar-menu */}
      <div id="explorar-menu" className="absolute top-0 left-0" />

      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-zinc-950 to-black pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        
        {/* Header section title on mobile & desktop */}
        <div className="text-center mb-6 sm:mb-10">
          <motion.h2 
            className="text-4xl sm:text-5xl md:text-6xl font-black bg-gradient-to-r from-white via-amber-200 to-orange-400 bg-clip-text text-transparent mb-3"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            Explorar Menú
          </motion.h2>
          <p className="text-gray-400 text-sm sm:text-base max-w-xl mx-auto">
            Descubre nuestra variedad de platillos artesanales preparados al instante
          </p>
        </div>

        {/* Sticky Category Tabs for Mobile & Desktop */}
        <div className="sticky top-0 z-30 bg-zinc-950/90 backdrop-blur-xl border-b border-zinc-800/80 py-3 -mx-4 px-4 sm:-mx-6 sm:px-6 mb-8 md:static md:bg-transparent md:border-none md:p-0 md:mb-12">
          <div className="flex overflow-x-auto no-scrollbar gap-2 sm:gap-3 md:flex-wrap md:justify-center py-1">
            {menuCategories.map((category, index) => {
              const isSelected = selectedCategory.id === category.id;
              return (
                <motion.button
                  key={category.id}
                  onClick={() => {
                    setSelectedCategory(category);
                    window.history.pushState(null, '', '#menu');
                  }}
                  className={`whitespace-nowrap px-4 py-2.5 sm:px-6 sm:py-3 text-sm sm:text-base font-bold rounded-full transition-all duration-300 flex-shrink-0 ${
                    isSelected
                      ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-black shadow-lg shadow-orange-500/40 scale-105'
                      : 'bg-zinc-900/90 text-gray-400 hover:bg-zinc-800 hover:text-white border border-zinc-800/60'
                  }`}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.04 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {category.name}
                </motion.button>
              );
            })}
          </div>
        </div>

        {/* Menu items grid */}
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedCategory.id}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.3 }}
          >
            {/* Selected Category Subtitle */}
            <motion.div 
              className="mb-6 sm:mb-8 text-center md:text-left border-l-4 border-orange-500 pl-4 py-1"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              <h3 className="text-2xl sm:text-3xl font-black text-white">
                {selectedCategory.name}
              </h3>
              <p className="text-gray-400 text-xs sm:text-sm mt-1">
                {selectedCategory.description}
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              {selectedCategory.items.map((item, index) => (
                <MenuItem key={item.id} item={item} index={index} />
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

const MenuItem = ({ item, index }) => {
  const [ref, inView] = useInView({
    threshold: 0.15,
    triggerOnce: true
  });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 25 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      className="group relative bg-zinc-900/90 border border-zinc-800/80 rounded-2xl overflow-hidden hover:shadow-2xl hover:shadow-orange-500/20 transition-all duration-300 flex flex-col sm:flex-row"
      whileHover={{ y: -3 }}
      whileTap={{ scale: 0.99 }}
    >
      <div className="relative h-44 sm:h-auto sm:w-2/5 overflow-hidden flex-shrink-0">
        <motion.img
          src={item.image}
          alt={item.name}
          className="w-full h-full object-cover"
          whileHover={{ scale: 1.08 }}
          transition={{ duration: 0.5 }}
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-transparent to-transparent sm:bg-gradient-to-r sm:from-transparent sm:to-zinc-900/90" />
        
        {item.featured && (
          <div className="absolute top-3 left-3 sm:top-3 sm:left-3 bg-gradient-to-r from-orange-500 to-amber-500 text-black px-3 py-1 rounded-full text-xs font-black shadow-md">
            Popular
          </div>
        )}
      </div>

      <div className="p-4 sm:p-5 flex-1 flex flex-col justify-between">
        <div>
          <div className="flex items-start justify-between gap-2 mb-2">
            <h4 className="text-lg sm:text-xl font-bold text-white group-hover:text-orange-400 transition-colors duration-300 leading-snug">
              {item.name}
            </h4>
            <span className="text-lg sm:text-xl font-black text-orange-400 whitespace-nowrap bg-orange-500/10 px-2.5 py-1 rounded-lg border border-orange-500/20">
              ${item.price}
            </span>
          </div>
          <p className="text-gray-400 text-xs sm:text-sm leading-relaxed">
            {item.description}
          </p>
        </div>
      </div>

      {/* Hover effect border glow */}
      <div className="absolute inset-0 border border-orange-500/0 group-hover:border-orange-500/40 rounded-2xl transition-all duration-300 pointer-events-none" />
    </motion.div>
  );
};

export default MenuSection;